import { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Textarea } from '@/components/ui/textarea';
import { CalendarOff, CalendarDays, Clock, Plus, X, Save, StickyNote, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

export default function AvailabilitySettingsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [settings, setSettings] = useState({
    monday: { enabled: true, max_events: 2 },
    tuesday: { enabled: true, max_events: 2 },
    wednesday: { enabled: true, max_events: 2 },
    thursday: { enabled: true, max_events: 2 },
    friday: { enabled: true, max_events: 3 },
    saturday: { enabled: true, max_events: 3 },
    sunday: { enabled: true, max_events: 2 },
    blackout_dates: [],
    advance_booking_days: 365,
    buffer_days: 7
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [newBlackoutDate, setNewBlackoutDate] = useState('');
  const [newBlackoutNote, setNewBlackoutNote] = useState('');
  const [editingBlackoutDate, setEditingBlackoutDate] = useState(null);
  const [editingNote, setEditingNote] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/availability-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch availability settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/availability-settings`, settings);
      toast.success('Availability settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateDaySettings = (day, field, value) => {
    setSettings({
      ...settings,
      [day]: {
        ...settings[day],
        [field]: value
      }
    });
  };

  // Helper to get date string from blackout entry (handles both old and new format)
  const getBlackoutDateStr = (entry) => {
    if (typeof entry === 'string') return entry;
    return entry?.date || '';
  };

  // Helper to get note from blackout entry
  const getBlackoutNote = (entry) => {
    if (typeof entry === 'string') return '';
    return entry?.note || '';
  };

  const addBlackoutDate = async () => {
    if (!newBlackoutDate) {
      toast.error('Please select a date');
      return;
    }
    const existingDates = settings.blackout_dates.map(getBlackoutDateStr);
    if (existingDates.includes(newBlackoutDate)) {
      toast.error('This date is already blocked');
      return;
    }
    try {
      await axios.post(`${API}/availability-settings/blackout`, {
        date: newBlackoutDate,
        note: newBlackoutNote
      });
      const newEntry = { date: newBlackoutDate, note: newBlackoutNote };
      setSettings({
        ...settings,
        blackout_dates: [...settings.blackout_dates, newEntry].sort((a, b) => 
          getBlackoutDateStr(a).localeCompare(getBlackoutDateStr(b))
        )
      });
      setNewBlackoutDate('');
      setNewBlackoutNote('');
      toast.success('Blackout date added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add blackout date');
    }
  };

  const removeBlackoutDate = async (date) => {
    try {
      await axios.delete(`${API}/availability-settings/blackout/${date}`);
      setSettings({
        ...settings,
        blackout_dates: settings.blackout_dates.filter(d => getBlackoutDateStr(d) !== date)
      });
      toast.success('Blackout date removed');
    } catch (error) {
      toast.error('Failed to remove blackout date');
    }
  };

  const updateBlackoutNote = async (date) => {
    try {
      await axios.put(`${API}/availability-settings/blackout/${date}`, {
        date: date,
        note: editingNote
      });
      setSettings({
        ...settings,
        blackout_dates: settings.blackout_dates.map(entry => {
          if (getBlackoutDateStr(entry) === date) {
            return { date, note: editingNote };
          }
          return entry;
        })
      });
      setEditingBlackoutDate(null);
      setEditingNote('');
      toast.success('Note updated');
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Convert blackout dates to Date objects for calendar highlighting
  const blackoutDatesAsDate = settings.blackout_dates.map(d => new Date(getBlackoutDateStr(d) + 'T00:00:00'));

  if (loading) {
    return (
      <div className="flex min-h-screen bg-obsidian">
        <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-obsidian" data-testid="availability-settings-page">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="availability-heading">
                Availability Settings
              </h1>
              <p className="text-muted-foreground">
                Configure your booking availability, limits, and blackout dates
              </p>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-neon-blue hover:bg-neon-blue/80 text-black"
              data-testid="save-availability-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Day of Week Settings */}
            <Card className="glass-card border-glass-border">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-neon-blue" />
                  Daily Booking Limits
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Set the maximum number of events you can handle each day of the week.
                </p>
                {DAYS_OF_WEEK.map((day) => (
                  <div 
                    key={day.key} 
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      settings[day.key]?.enabled 
                        ? 'border-glass-border bg-white/5' 
                        : 'border-red-500/30 bg-red-500/5'
                    }`}
                    data-testid={`day-settings-${day.key}`}
                  >
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={settings[day.key]?.enabled ?? true}
                        onCheckedChange={(checked) => updateDaySettings(day.key, 'enabled', checked)}
                        data-testid={`day-enabled-${day.key}`}
                      />
                      <span className={`font-medium ${!settings[day.key]?.enabled ? 'text-muted-foreground line-through' : ''}`}>
                        {day.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Max events:</span>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={settings[day.key]?.max_events ?? 2}
                        onChange={(e) => updateDaySettings(day.key, 'max_events', parseInt(e.target.value) || 1)}
                        className="w-16 bg-muted/20 border-glass-border text-center"
                        disabled={!settings[day.key]?.enabled}
                        data-testid={`day-max-events-${day.key}`}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Booking Window Settings */}
            <Card className="glass-card border-glass-border">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Clock className="w-5 h-5 text-neon-purple" />
                  Booking Window
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Control how far in advance clients can book, and minimum notice required.
                </p>
                
                <div className="space-y-2">
                  <Label>Minimum Notice (Buffer Days)</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="0"
                      max="90"
                      value={settings.buffer_days}
                      onChange={(e) => setSettings({ ...settings, buffer_days: parseInt(e.target.value) || 0 })}
                      className="w-24 bg-muted/20 border-glass-border"
                      data-testid="buffer-days-input"
                    />
                    <span className="text-sm text-muted-foreground">days before event</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Clients must book at least this many days in advance
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Advance Booking Limit</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="30"
                      max="730"
                      value={settings.advance_booking_days}
                      onChange={(e) => setSettings({ ...settings, advance_booking_days: parseInt(e.target.value) || 365 })}
                      className="w-24 bg-muted/20 border-glass-border"
                      data-testid="advance-days-input"
                    />
                    <span className="text-sm text-muted-foreground">days into the future</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Maximum how far in advance clients can book
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-neon-blue/10 border border-neon-blue/30">
                  <p className="text-sm">
                    <strong>Current booking window:</strong> {settings.buffer_days} to {settings.advance_booking_days} days from today
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Blackout Dates */}
            <Card className="glass-card border-glass-border lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <CalendarOff className="w-5 h-5 text-red-500" />
                  Blackout Dates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-6">
                  Block specific dates when you're unavailable (vacations, holidays, personal events).
                </p>
                
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Add Blackout Date */}
                  <div className="space-y-4">
                    <Label>Add New Blackout Date</Label>
                    <div className="flex gap-2">
                      <Input
                        type="date"
                        value={newBlackoutDate}
                        onChange={(e) => setNewBlackoutDate(e.target.value)}
                        className="bg-muted/20 border-glass-border flex-1"
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="blackout-date-input"
                      />
                      <Button
                        onClick={addBlackoutDate}
                        className="bg-red-500 hover:bg-red-600 text-white"
                        data-testid="add-blackout-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Block
                      </Button>
                    </div>
                    
                    {/* Note input */}
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Note (optional)</Label>
                      <Input
                        value={newBlackoutNote}
                        onChange={(e) => setNewBlackoutNote(e.target.value)}
                        placeholder="e.g., Family vacation, Holiday, Personal day..."
                        className="bg-muted/20 border-glass-border"
                        data-testid="blackout-note-input"
                      />
                    </div>
                    
                    {/* Calendar Preview */}
                    <div className="flex justify-center p-4 bg-white/5 rounded-lg border border-glass-border">
                      <Calendar
                        mode="multiple"
                        selected={blackoutDatesAsDate}
                        className="rounded-md"
                        disabled={(date) => date < new Date()}
                        modifiers={{
                          blackout: blackoutDatesAsDate
                        }}
                        modifiersStyles={{
                          blackout: { backgroundColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }
                        }}
                      />
                    </div>
                  </div>

                  {/* Current Blackout Dates List */}
                  <div className="space-y-4">
                    <Label>Blocked Dates ({settings.blackout_dates.length})</Label>
                    <div className="max-h-[400px] overflow-y-auto space-y-2 pr-2">
                      {settings.blackout_dates.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <CalendarOff className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No blackout dates set</p>
                          <p className="text-sm">Add dates when you're unavailable</p>
                        </div>
                      ) : (
                        settings.blackout_dates.map((entry) => {
                          const dateStr = getBlackoutDateStr(entry);
                          const note = getBlackoutNote(entry);
                          const isEditing = editingBlackoutDate === dateStr;
                          
                          return (
                            <div
                              key={dateStr}
                              className="p-3 rounded-lg bg-red-500/10 border border-red-500/30"
                              data-testid={`blackout-date-${dateStr}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <CalendarOff className="w-4 h-4 text-red-500" />
                                  <span className="font-medium">{formatDate(dateStr)}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      if (isEditing) {
                                        setEditingBlackoutDate(null);
                                        setEditingNote('');
                                      } else {
                                        setEditingBlackoutDate(dateStr);
                                        setEditingNote(note);
                                      }
                                    }}
                                    className="text-muted-foreground hover:text-foreground h-7 w-7 p-0"
                                    data-testid={`edit-note-${dateStr}`}
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeBlackoutDate(dateStr)}
                                    className="text-red-500 hover:text-red-400 hover:bg-red-500/20 h-7 w-7 p-0"
                                    data-testid={`remove-blackout-${dateStr}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Note display or edit */}
                              {isEditing ? (
                                <div className="mt-2 flex gap-2">
                                  <Input
                                    value={editingNote}
                                    onChange={(e) => setEditingNote(e.target.value)}
                                    placeholder="Add a note..."
                                    className="bg-muted/20 border-glass-border text-sm flex-1"
                                    data-testid={`edit-note-input-${dateStr}`}
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => updateBlackoutNote(dateStr)}
                                    className="bg-neon-blue hover:bg-neon-blue/80 text-black"
                                  >
                                    <Save className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : note ? (
                                <div className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
                                  <StickyNote className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  <span>{note}</span>
                                </div>
                              ) : null}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
